import classNames from 'classnames';
import { useEffect, useRef, useState } from 'react';

import {
  SubscriptionSelection,
  SubscriptionConfirmation,
  SubscriptionDirectPayment,
} from './Steps';
import SubscriptionUnlimitedCheckout from './Steps/SubscriptionUnlimitedCheckout';
import SubscriptionUnlimitedCheckoutLegacy from './Steps/SubscriptionUnlimitedCheckout/SubscriptionUnlimitedCheckoutLegacy';
import {
  CHECKOUT_MODE_EVENT_NAME,
  SUBSCRIPTION_UNLIMITED_CHECKOUT_MODES,
  getSubscriptionUnlimitedCheckoutMode,
  installSubscriptionUnlimitedCheckoutWindowApi,
} from './Steps/SubscriptionUnlimitedCheckout/subscriptionUnlimitedCheckoutMode';
import { SubscriptionNavbar } from './shared';
import { SubscriptionProvider, useSubscription } from './SubscriptionProvider';
import { SUBSCRIPTION_STEPS } from '../../utils/subscriptions';
import { useFirebaseAuth } from '../../../global/FirebaseProvider/FirebaseProvider';
import LoadingBar from '../../global/LoadingBar';
import { ARTSAI_PIXEL_ID } from '../../config/config';
import { ROUTE_TAKE_BACK_BAGS } from '../../config/routes';
import useNavigateWithSearch from '../../../../hooks/useNavigateWithSearch';

import './Subscription.scss';

const SUBSCRIPTION_FLOW_STEP_ORDER = [
  SUBSCRIPTION_STEPS.SELECTION,
  SUBSCRIPTION_STEPS.PAYMENT,
  SUBSCRIPTION_STEPS.DIRECT_PAYMENT,
  SUBSCRIPTION_STEPS.CONFIRMATION,
];

const getSubscriptionFlowStepIndex = (s) => {
  const i = SUBSCRIPTION_FLOW_STEP_ORDER.indexOf(s);
  return i === -1 ? SUBSCRIPTION_FLOW_STEP_ORDER.length : i;
};

const SubscriptionContainer = () => (
  <SubscriptionProvider>
    <Subscription />
  </SubscriptionProvider>
);

const Subscription = () => {
  const {
    isLoading: isLoadingFirebaseAuth,
    isSignedIn,
    userData,
  } = useFirebaseAuth();
  const {
    isLoading: isLoadingConfig,
    isFromCheckout,
    step,
    contentScrollRef,
  } = useSubscription();
  const navigateWithSearch = useNavigateWithSearch();

  const [hasMounted, setHasMounted] = useState(false);
  const [checkoutMode, setCheckoutMode] = useState(
    () => getSubscriptionUnlimitedCheckoutMode(),
  );
  const flowAnimRef = useRef({ step, direction: 'forward' });
  if (flowAnimRef.current.step !== step) {
    const prevStep = flowAnimRef.current.step;
    const prevIdx = getSubscriptionFlowStepIndex(prevStep);
    const nextIdx = getSubscriptionFlowStepIndex(step);
    flowAnimRef.current = {
      step,
      direction: nextIdx < prevIdx ? 'back' : 'forward',
    };
  }
  const flowDirection = flowAnimRef.current.direction;

  useEffect(() => {
    installSubscriptionUnlimitedCheckoutWindowApi();

    const handleCheckoutModeChange = () => {
      setCheckoutMode(getSubscriptionUnlimitedCheckoutMode());
    };

    window.addEventListener(CHECKOUT_MODE_EVENT_NAME, handleCheckoutModeChange);

    return () => {
      window.removeEventListener(CHECKOUT_MODE_EVENT_NAME, handleCheckoutModeChange);
    };
  }, []);

  useEffect(() => {
    if (
      !isFromCheckout && !isLoadingFirebaseAuth && isSignedIn &&
      userData?.email && userData?.activeSubscription
    ) {
      navigateWithSearch(ROUTE_TAKE_BACK_BAGS);
    }

    if (!isLoadingFirebaseAuth && !isLoadingConfig) {
      setHasMounted(true);
    }
  }, [
    isFromCheckout,
    isLoadingFirebaseAuth,
    isSignedIn,
    userData?.email,
    userData?.activeSubscription,
  ]);

  const isLoading = !hasMounted && (isLoadingFirebaseAuth || isLoadingConfig);
  const UnlimitedCheckoutComponent = checkoutMode === SUBSCRIPTION_UNLIMITED_CHECKOUT_MODES.NEW
    ? SubscriptionUnlimitedCheckout
    : SubscriptionUnlimitedCheckoutLegacy;

  return (
    <div className="Subscription">
      {step !== SUBSCRIPTION_STEPS.PAYMENT && step !== SUBSCRIPTION_STEPS.CONFIRMATION && (
        <SubscriptionNavbar />
      )}
      <div ref={contentScrollRef} className="Subscription__container">
        {isLoading ? (
          <LoadingBar className="Subscription__container-loader" />
        ) : (
          <>
            <img src={`https://arttrk.com/pixel/?ad_log=referer&action=signup&pixid=${ARTSAI_PIXEL_ID}`} width="1" height="1" border="0" alt="" />
            <div
              key={step}
              className={classNames(
                'SubscriptionFlowPanel',
                flowDirection === 'back' ? 'SubscriptionFlowPanel--back' : 'SubscriptionFlowPanel--forward',
                step === SUBSCRIPTION_STEPS.PAYMENT && 'SubscriptionFlowPanel--unlimitedCheckout',
              )}
            >
              {step === SUBSCRIPTION_STEPS.SELECTION && <SubscriptionSelection />}
              {step === SUBSCRIPTION_STEPS.PAYMENT && <UnlimitedCheckoutComponent />}
              {step === SUBSCRIPTION_STEPS.DIRECT_PAYMENT && <SubscriptionDirectPayment />}
              {step === SUBSCRIPTION_STEPS.CONFIRMATION && <SubscriptionConfirmation />}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SubscriptionContainer;
